import type { ReactNode } from 'react';

/**
 * Tabla densa.
 *
 * Sin bordes verticales y con separadores tenues: las líneas que se cruzan cansan la vista
 * y no agregan información. Las columnas de números se alinean a la derecha y con cifras
 * tabulares, que es lo que permite comparar importes de un vistazo.
 *
 * El encabezado va **en mayúsculas chicas y espaciadas**: se distingue de los datos sin una línea
 * gruesa ni un fondo gris, que son las dos formas de gastar contraste en algo que se lee una vez.
 * Y el separador de filas es el borde tenue: con doscientas filas de existencias, el borde normal
 * dibuja una reja.
 */
export interface Columna<T> {
  readonly clave: string;
  readonly titulo: ReactNode;
  readonly numerica?: boolean;
  readonly contenido: (fila: T) => ReactNode;
}

export function Tabla<T>({
  columnas,
  filas,
  claveDeFila,
  vacio,
}: {
  readonly columnas: readonly Columna<T>[];
  readonly filas: readonly T[];
  readonly claveDeFila: (fila: T) => string;
  readonly vacio?: ReactNode;
}): React.ReactElement {
  if (filas.length === 0 && vacio !== undefined) return <>{vacio}</>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-cuerpo">
        <thead>
          <tr className="border-b border-borde text-left">
            {columnas.map((columna) => (
              <th
                key={columna.clave}
                scope="col"
                className={`py-1.5 pr-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-texto-tenue ${
                  columna.numerica === true ? 'text-right' : ''
                }`}
              >
                {columna.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => (
            <tr
              key={claveDeFila(fila)}
              className="border-b border-borde-tenue transition-colors last:border-0 hover:bg-acento-suave"
            >
              {columnas.map((columna) => (
                <td
                  key={columna.clave}
                  className={`py-1.5 pr-3 align-middle ${
                    columna.numerica === true ? 'numero text-right tabular-nums' : ''
                  }`}
                >
                  {columna.contenido(fila)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
